import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Download, Headset, ImageOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { FadeUp } from "@/components/landing/Reveal";
import { ConsultantDialog } from "@/components/ConsultantDialog";
import { api, fileUrl, RISK } from "@/lib/api";
import { downloadAuditPdf } from "@/lib/pdf";
import { ActionPlan } from "@/components/ActionPlan";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { useAuth } from "@/context/AuthContext";

const LABEL = { si: ["Sí", "text-emerald-400"], no: ["No", "text-rose-400"], na: ["N/A", "text-[#F5F1E8]/60"] };

const ScoreRing = ({ score, color }) => {
  const C = 2 * Math.PI * 88;
  return (
    <div className="relative w-56 h-56 sm:w-64 sm:h-64" data-testid="summary-score-ring">
      <svg viewBox="0 0 200 200" className="w-full h-full -rotate-90">
        <circle cx="100" cy="100" r="88" stroke="rgba(245,241,232,0.08)" strokeWidth="10" fill="none" />
        <motion.circle cx="100" cy="100" r="88" stroke={color} strokeWidth="10" fill="none" strokeDasharray={C}
          initial={{ strokeDashoffset: C }} animate={{ strokeDashoffset: C * (1 - score / 100) }} transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1], delay: 0.2 }} />
      </svg>
      <div className="absolute inset-0 grid place-items-center text-center">
        <div>
          <div className="font-display-x text-6xl sm:text-7xl font-black tracking-tighter" data-testid="summary-score-value">{score}<span className="text-3xl" style={{ color }}>%</span></div>
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#F5F1E8]/60">cumplimiento</div>
        </div>
      </div>
    </div>
  );
};

export default function SummaryPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [audit, setAudit] = useState(null);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => {
    api.get(`/audits/${id}`).then((r) => setAudit(r.data)).catch(() => setError(true));
  }, [id]);

  const onPdf = async () => {
    setPdfBusy(true);
    try { await downloadAuditPdf(audit, audit.client_logo); toast.success("Reporte PDF descargado"); }
    catch { toast.error("No se pudo generar el PDF"); }
    finally { setPdfBusy(false); }
  };

  if (error) return (<main className="min-h-screen"><Navbar variant="app" /><div className="pt-40 px-8 text-center" data-testid="summary-error">Auditoría no encontrada. <Link to="/panel" className="text-[#F59E0B] underline">Volver al panel</Link></div></main>);
  if (!audit) return (<main className="min-h-screen grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[#F59E0B]" /></main>);

  const r = RISK[audit.risk] ?? RISK.Alto;
  const photos = (audit.answers ?? []).filter((a) => a.photo_path);
  const improved = (audit.resolved ?? 0) > 0;
  const waMsg = `Hola Ascentra, soy de ${audit.company} (${audit.site}). Mi auditoría Vigía (folio ${audit.id.slice(-6).toUpperCase()}) tiene ${audit.score}% de cumplimiento, riesgo ${audit.risk.toLowerCase()}, y ${audit.no - (audit.resolved ?? 0)} hallazgos pendientes. Me gustaría asesoría.`;

  return (
    <main className="min-h-screen" data-testid="summary-page">
      <Navbar variant="app" />
      <div className="mx-auto max-w-[1200px] px-5 sm:px-8 pt-28 pb-24">
        <Link to={isAdmin ? "/admin" : "/panel"} data-testid="summary-back-link" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-[#F5F1E8]/70 hover:text-[#F59E0B]"><ArrowLeft className="w-4 h-4" /> {isAdmin ? "Bandeja de solicitudes" : "Mis auditorías"}</Link>

        <section className="mt-10 grid lg:grid-cols-12 gap-10 items-center border border-white/10 bg-[#111722] p-6 sm:p-10 relative overflow-hidden">
          <div className="absolute inset-x-0 top-0 h-1" style={{ background: r.color }} />
          <div className="lg:col-span-7">
            <div className="font-mono text-xs uppercase tracking-[0.22em] text-[#F59E0B]" data-testid="summary-module-name">{audit.module_name} · {audit.module_subtitle}</div>
            <h1 className="font-display mt-4 text-3xl sm:text-5xl font-extrabold tracking-tight leading-[1.02]" data-testid="summary-company">{audit.company}</h1>
            <p className="mt-2 text-[#F5F1E8]/70">{audit.site} · {audit.auditor} · {new Date(audit.created_at).toLocaleDateString("es-MX", { dateStyle: "long" })}</p>
            <div className={`mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full border font-semibold ${r.bg} ${r.border} ${r.text}`} data-testid="summary-risk-badge">
              <span className="w-2 h-2 rounded-full" style={{ background: r.color }} /> {r.label}
            </div>
            {improved && (
              <p className="mt-4 text-sm text-[#F5F1E8]/75" data-testid="summary-score-evolution">
                Score recalculado: <span className="font-mono line-through text-[#F5F1E8]/50">{audit.initial_score}% · {audit.initial_risk}</span> <span className="text-emerald-400 font-semibold">→ {audit.score}% · {audit.risk}</span>
              </p>
            )}
            <div className="mt-8 grid grid-cols-4 gap-px bg-white/10 border border-white/10 max-w-lg">
              {[["Sí", audit.yes, "text-emerald-400"], ["No", audit.no, "text-rose-400"], ["N/A", audit.na, "text-[#F5F1E8]/70"], ["Resueltos", audit.resolved ?? 0, "text-sky-300"]].map(([k, v, c]) => (
                <div key={k} className="bg-[#111722] p-4" data-testid={`summary-count-${k}`}><div className={`font-display-x text-3xl font-black ${c}`}>{v}</div><div className="font-mono text-xs text-[#F5F1E8]/60">{k}</div></div>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={onPdf} disabled={pdfBusy} data-testid="download-pdf-button" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 hover:border-[#F59E0B] hover:text-[#F59E0B] transition-colors font-semibold text-sm disabled:opacity-50">
                {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Descargar PDF
              </button>
              {!isAdmin && (
                <button onClick={() => setOpen(true)} data-testid="contact-consultant-button" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#F59E0B] text-[#0B0F17] hover:bg-[#F5F1E8] transition-colors font-semibold text-sm">
                  <Headset className="w-4 h-4" /> Contactar a un consultor
                </button>
              )}
              {!isAdmin && <WhatsAppButton message={waMsg} testid="summary-whatsapp-button" />}
            </div>
          </div>
          <div className="lg:col-span-5 flex justify-center"><ScoreRing score={audit.score} color={r.color} /></div>
        </section>

        <ActionPlan items={audit.action_plan ?? []} auditId={audit.id} onAudit={isAdmin ? null : (a) => setAudit((prev) => ({ ...prev, ...a }))} whatsappMessage={isAdmin ? null : waMsg} />

        <section className="mt-16 grid lg:grid-cols-12 gap-10">
          <FadeUp className="lg:col-span-5">
            <h2 className="font-display text-2xl font-bold">Cumplimiento por bloque</h2>
            <div className="mt-6 space-y-5" data-testid="summary-blocks">
              {(audit.blocks ?? []).map((b) => (
                <div key={b.id}>
                  <div className="flex justify-between text-sm"><span>{b.title}</span><span className="font-mono">{b.score == null ? "N/A" : `${b.score}%`}</span></div>
                  <div className="font-mono text-[11px] text-[#F5F1E8]/55">{b.code}</div>
                  <div className="mt-1.5 h-1.5 bg-white/5"><motion.div initial={{ width: 0 }} whileInView={{ width: `${b.score ?? 0}%` }} viewport={{ once: true }} transition={{ duration: 1 }} className="h-full" style={{ background: b.score == null ? "transparent" : b.score >= 80 ? "#10B981" : b.score >= 60 ? "#F59E0B" : "#F43F5E" }} /></div>
                </div>
              ))}
            </div>
          </FadeUp>
          <FadeUp delay={0.1} className="lg:col-span-7">
            <h2 className="font-display text-2xl font-bold">Evidencia fotográfica</h2>
            {photos.length === 0 ? (
              <div className="mt-6 border border-dashed border-white/15 p-10 flex items-center gap-4 text-[#F5F1E8]/65" data-testid="summary-no-photos"><ImageOff className="w-6 h-6" /> No se cargaron fotos de evidencia.</div>
            ) : (
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="summary-photos">
                {photos.map((a) => (
                  <figure key={a.question_id} className="group border border-white/10 bg-[#111722]" data-testid={`summary-photo-${a.question_id}`}>
                    <div className="aspect-[4/3] overflow-hidden"><img src={fileUrl(a.photo_path)} alt={a.text} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" /></div>
                    <figcaption className="p-4 text-sm text-[#F5F1E8]/80"><span className="font-mono text-rose-400 mr-2">{a.question_id.toUpperCase()}</span>{a.text}</figcaption>
                  </figure>
                ))}
              </div>
            )}
          </FadeUp>
        </section>

        <FadeUp className="mt-16">
          <h2 className="font-display text-2xl font-bold">Respuestas</h2>
          <div className="mt-6 border-t border-white/10" data-testid="summary-answers">
            {(audit.answers ?? []).map((a, i) => (
              <div key={a.question_id} className="grid grid-cols-12 gap-4 py-4 border-b border-white/10 text-sm">
                <span className="col-span-2 sm:col-span-1 font-mono text-[#F5F1E8]/55">{String(i + 1).padStart(2, "0")}</span>
                <span className="col-span-8 sm:col-span-10">{a.text}</span>
                <span className={`col-span-2 sm:col-span-1 text-right font-semibold ${LABEL[a.answer]?.[1]}`}>{LABEL[a.answer]?.[0]}</span>
              </div>
            ))}
          </div>
        </FadeUp>
      </div>
      <ConsultantDialog open={open} onOpenChange={setOpen} audit={audit} />
    </main>
  );
}
